/**
 * useBulkActivityAction — la sélection multiple d'une liste d'activités, et son geste de masse.
 *
 * Archiver (l'onglet) et restaurer (les archives) sont le MÊME geste : une sélection, une question
 * qui compte les lignes, un POST portant `{ student }` en route et `{ activity_ids }` en corps, un
 * accusé qui les recompte. Seuls le service, les mots, et ce qu'il faut rejouer après changent —
 * le réducteur retire les activités archivées de la liste tout seul (`currentUser.js:1585-1607`),
 * alors que la restauration demande de recharger la page 1 (`ArchivedActivitiesScreen.js:117-124`).
 */
import { useCallback, useMemo, useReducer, useRef, useState } from "react"

import { type UIActivity } from "../../../types"
import { type GestureName } from "../../../hooks/gestureTracking"
import { type ApiService } from "../../../hooks/useApiFetch"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { initialSelectionState, selectionReducer, type ActivityId } from "./activitySelectionReducer"

export type BulkActivityConfirm = {
    title: string
    message: string
    count: number
}

/** Ce que l'écran met de ses propres mots ; le compte lui est rendu pour qu'il l'accorde. */
export type BulkActivityWords = {
    confirmTitle: (count: number) => string
    confirmMessage: string
    doneMessage: (count: number) => string
    failedMessage: string
}

export type UseBulkActivityActionArgs = {
    studentId: string | number | null
    activities: UIActivity[]
    service: ApiService
    words: BulkActivityWords
    /** Rejoué après le succès quand la liste ne se met pas à jour d'elle-même. */
    onDone?: () => void
    /** Le geste mesuré. Défaut : le nom du service (`useApiMutation`). */
    gesture?: GestureName
}

export function useBulkActivityAction(args: UseBulkActivityActionArgs) {
    const { studentId, activities, service, words, onDone, gesture } = args

    const [selection, dispatchSelection] = useReducer(selectionReducer, initialSelectionState)
    const [confirm, setConfirm] = useState<BulkActivityConfirm | null>(null)
    const [snackbar, setSnackbar] = useState<{ message: string; tone: "success" | "danger" } | null>(null)

    /** Combien de lignes partent : l'accusé les compte, et la sélection est déjà refermée. */
    const pendingCountRef = useRef(0)

    const { start, requesting } = useApiMutation({
        service,
        fallback: words.failedMessage,
        gesture,
        // La tranche renvoie ses `routeParams` : on ne réagit qu'à l'appel qu'on a lancé.
        scopeParam: "student",
        onSuccess: () => {
            dispatchSelection({ type: "CANCEL" })
            setSnackbar({ message: words.doneMessage(pendingCountRef.current), tone: "success" })
            onDone?.()
        },
        onError: (message) => setSnackbar({ message, tone: "danger" }),
    })

    const run = useCallback(() => {
        const ids = selection.selectedIds
        setConfirm(null)
        if (studentId == null || ids.length === 0) return
        pendingCountRef.current = ids.length
        start({ student: studentId }, { activity_ids: ids })
    }, [start, selection.selectedIds, studentId])

    const actions = useMemo(
        () => ({
            startSelection: (id?: ActivityId) => dispatchSelection({ type: "START", id }),
            toggleSelect: (id: ActivityId) => dispatchSelection({ type: "TOGGLE", id }),
            toggleSelectAll: () => dispatchSelection({ type: "TOGGLE_ALL", ids: activities.map((a) => a.id) }),
            cancelSelection: () => dispatchSelection({ type: "CANCEL" }),
            ask: () => {
                const count = selection.selectedIds.length
                setConfirm({ count, title: words.confirmTitle(count), message: words.confirmMessage })
            },
            cancelConfirm: () => setConfirm(null),
            run,
            dismissSnackbar: () => setSnackbar(null),
        }),
        [activities, run, selection.selectedIds.length, words]
    )

    return { selection, confirm, pending: requesting, snackbar, actions }
}
